import React, { createContext, useEffect, useState } from "react";
import { ISpecificPlanProviderProps } from "../types/interfaces/ISpecificPlanProviderProps";
import { ISpecificPlanContextType } from "../types/interfaces/ISpecificPlanContextType";
import { IIndividualLearningPlan } from "../types/interfaces/IIndividualLearningPlan";
import { useAuthorizationContext } from "../../global/hooks/useAuthorizationContext";
import { useDuesSponsoredOrgContext } from '../../global/hooks/useDuesSponsoredOrgContext';
import { useErrorContext } from "../../global/hooks/useErrorContext";
import { useIlpContext } from "../hooks/useIlpContext";
import useGetPlan from "../hooks/useGetPlan";
import { getIlpKeyFromQueryString } from "../../global/utilities/ProcessQueryString";

//The specific context context object holds the latest value of the state.
export const SpecificPlanContext = createContext<ISpecificPlanContextType | undefined>(undefined);

//This component is responsible to get specific plan.
export const SpecificPlanProvider: React.FC<ISpecificPlanProviderProps> = ({ children }) => {
    const [individualLearningPlan, setIndividualLearningPlan] = useState<IIndividualLearningPlan | null>(null);
    const [loadingSpecificPlanCtx, setLoadingSpecificPlanCtx] = useState<boolean>(true);
    const { roles } = useAuthorizationContext();
    const { organizations, setSelectedOrganization } = useDuesSponsoredOrgContext();
    const { setRole, setIsAdminFlag, setIsAdvisorFlag } = useIlpContext();
    const [getPlanFetch] = useGetPlan();
    const { setError } = useErrorContext();
    const ilpKey = getIlpKeyFromQueryString();
    const allRoles = roles!.includes(process.env.REACT_APP_NFROLE_COORDINATOR!) ? roles
                    + "," + process.env.REACT_APP_NFROLE_ASSISTANT_COORDINATOR
                    + "," + process.env.REACT_APP_NFROLE_DIRECTOR
                    + "," + process.env.REACT_APP_NFROLE_ADMINISTRATOR
                    + "," + process.env.REACT_APP_NFROLE_ASSOCIATE_DIRECTOR
                    : roles;

    const handleError = (message: string | null, statusCode: number) => {
        setError({ message, statusCode });
        setLoadingSpecificPlanCtx(false);
    };

    const setOrganization = (ilpPlan: IIndividualLearningPlan | null) => {
        if (ilpPlan && ilpPlan?.LearningPlan.OrgCustomerKey) {
            const org = organizations?.find(org => org.OrganizationKey?.toLowerCase() === ilpPlan?.LearningPlan.OrgCustomerKey.toLowerCase());
            if (org) {
                if (allRoles != null && allRoles.length > 1 && allRoles.includes(org.Title!)) {
                    setSelectedOrganization(org);
                    setIndividualLearningPlan(ilpPlan);
                    if (org.Title == process.env.REACT_APP_NFROLE_ASSISTANT_COORDINATOR || org.Title == process.env.REACT_APP_NFROLE_DIRECTOR ||
                        org.Title == process.env.REACT_APP_NFROLE_COORDINATOR || org.Title == process.env.REACT_APP_NFROLE_ADMINISTRATOR ||
                        org.Title == process.env.REACT_APP_NFROLE_ASSOCIATE_DIRECTOR) {
                        setIsAdminFlag(true);
                        setRole(org.Title);
                    }
                    else if (org.Title == process.env.REACT_APP_NFROLE_ADVISOR) {
                        setIsAdvisorFlag(true);
                        setRole(org.Title);
                    }
                }
                else {
                    setIsAdminFlag(false);
                    setIsAdvisorFlag(false);
                }
            }
            else {
                setIsAdminFlag(false);
                setIsAdvisorFlag(false);
            }
       }
    };

    useEffect(() => {
        try {
            if (ilpKey != null) {
                const fetchPlanAndSetPlan = async () => {
                    setLoadingSpecificPlanCtx(true);
                    const fetchedPlan = await getPlanFetch(ilpKey);
                    setOrganization(fetchedPlan);
                    setLoadingSpecificPlanCtx(false);
                };
                fetchPlanAndSetPlan();
            }
            else {
                setLoadingSpecificPlanCtx(false);
            }
        }
        catch (error: any) {
            console.log('get error', error);
            handleError(null, error.status);
        }
    }, [ilpKey]);

    const value = {
        individualLearningPlan,
        loadingSpecificPlanCtx,
        setIndividualLearningPlan
    }

    return (
        <SpecificPlanContext.Provider value={value}>
            {!loadingSpecificPlanCtx && (children)}
        </SpecificPlanContext.Provider>
    );
}